// pages/SAPProcessMapIssues.jsx — "Past issues & solutions" for the selected Process Map step.
// Lists Solman tickets that match the step (GET /api/sap-procmap/issues) with the core team's
// replies, so a new user issue can be solved by looking at how the same problem was handled.
import { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { issueKeywords } from './sapProcessMapIssueKw';

export default function SAPProcessMapIssues({ landKey, flowKey, step, coCode, color, lang }) {
  const id = lang === 'id';
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const kw = issueKeywords(flowKey, step);

  useEffect(() => {
    let live = true;
    setD(null); setErr(null); setOpen(null);
    api.get('/api/sap-procmap/issues', { params: { land: landKey, kw: kw.join(','), limit: 60 } })
      .then(r => { if (live) setD(r.data); })
      .catch(e => { if (live) setErr(e?.response?.data?.error || e.message); });
    return () => { live = false; };
  }, [landKey, flowKey, step.id]);

  const ql = q.trim().toLowerCase();
  const list = (d?.tickets || []).filter(t => !ql || (t.description + ' ' + t.texts.map(x => x.text).join(' ')).toLowerCase().includes(ql));

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-4" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="flex flex-wrap items-baseline gap-2">
        <div className="text-[14px] font-bold text-slate-800">{id ? 'Issue & solusi sebelumnya' : 'Past issues & solutions'}</div>
        <div className="text-[12px] text-slate-500">
          {d ? (id ? `${d.total} tiket Solman cocok` : `${d.total} matching Solman tickets`) : ''}
          {d && d.total > (d.tickets || []).length ? (id ? ` · ${d.tickets.length} terbaru ditampilkan` : ` · latest ${d.tickets.length} shown`) : ''}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={id ? 'Cari di tiket & jawaban…' : 'Search tickets & replies…'}
          className="ml-auto border border-slate-200 rounded-lg px-2.5 py-1 text-[12.5px] w-full sm:w-64" />
      </div>
      <div className="text-[11px] text-slate-400 mt-1">
        {id ? 'Dicocokkan dengan: ' : 'Matched on: '}<span className="font-mono">{kw.join(' · ')}</span>
        {coCode ? (id ? ` · semua company (filter ${coCode} tidak berlaku)` : ` · all companies (the ${coCode} filter does not apply)`) : ''}
      </div>
      {err && <p className="text-[12.5px] text-red-600 mt-2">{err}</p>}
      {!d && !err && <p className="text-[12.5px] text-slate-400 mt-2">{id ? 'Memuat…' : 'Loading…'}</p>}
      {d && d.note && <p className="text-[12.5px] text-slate-500 mt-2">{d.note}</p>}
      {d && !d.note && !list.length && <p className="text-[12.5px] text-slate-500 mt-2">{id ? 'Tidak ada tiket yang cocok.' : 'No matching tickets.'}</p>}
      <div className="mt-2 divide-y divide-slate-100">
        {list.map(t => {
          const replies = t.texts.filter(x => x.type !== 'Description');
          const desc = t.texts.find(x => x.type === 'Description');
          const on = open === t.ticket_id;
          return (
            <div key={t.ticket_id} className="py-2">
              <button type="button" onClick={() => setOpen(on ? null : t.ticket_id)} className="w-full text-left flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono text-[11.5px] text-slate-400">{t.ticket_id}</span>
                <span className="text-[13px] font-semibold text-slate-700">{t.description}</span>
                <span className="text-[11.5px] text-slate-400">{t.company} · {t.created_on} · {t.sap_component || '-'} · {t.status}</span>
                <span className="ml-auto text-[11.5px]" style={{ color }}>{replies.length ? (id ? `${replies.length} jawaban` : `${replies.length} replies`) : (id ? 'tanpa jawaban' : 'no replies')} {on ? '▲' : '▼'}</span>
              </button>
              {on && (
                <div className="mt-2 grid gap-2">
                  {desc && <div className="bg-slate-50 rounded-lg p-2.5 text-[12.5px] text-slate-600 whitespace-pre-wrap"><b>{id ? 'Permintaan' : 'Request'}</b> · {desc.by}<br />{desc.text}</div>}
                  {replies.map((x, i) => (
                    <div key={i} className="rounded-lg p-2.5 text-[12.5px] text-slate-700 whitespace-pre-wrap" style={{ background: color + '12' }}>
                      <b>{x.type === 'Solution' ? (id ? 'Solusi' : 'Solution') : (id ? 'Jawaban' : 'Reply')}</b> · {x.by} · {x.on}<br />{x.text}
                    </div>
                  ))}
                  {!t.texts.length && <p className="text-[12px] text-slate-400 m-0">{id ? 'Teks tiket belum tersinkron.' : 'Ticket text not synced yet.'} {t.processor ? `· ${t.processor}` : ''}</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
